import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Box, CircularProgress, MenuItem, Typography } from "@mui/material";
import BossesTable from "../Components/BossesTable";
import SearchBar from "../Components/SearchBar";
import Pagination from "../Components/Pagination";
import BossDetailsModal from "../Components/SideModal";
import { GetAllData } from "../NetworkCalls/Products/ServerReq";
import { apiUrl, axiosInstance } from "../axios/index";

const PageContainer = styled('div')(({ theme }) => ({
  padding: "16px",
  backgroundColor: "#f5f6fe",
  minHeight: "100vh",
  marginLeft: "40px",
  width: "calc(100% - 40px)",
  marginTop: "64px",

  '@media (max-width: 768px)': {
    marginRight: "auto",
    marginLeft: "auto",
    width: "100%",
    padding: "12px"
  }
}));

const Title = styled.h1`
  font-family: 'Urbanist';
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 24px;
`;

export const BossesList = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [pageData, setPageData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [selectedBoss, setSelectedBoss] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [industryLoading, setIndustryLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false); // New state to track if user is searching

  const columns = [
    { key: "bossName", label: "Boss Name" },
    { key: "email", label: "Email" },
    { key: "companyName", label: "Company" },
    { key: "industry", label: "Industry" },
    { key: "action", label: "Recommendation" }
  ];

  // Fetch industry options from API
  useEffect(() => {
    const fetchIndustryOptions = async () => {
      try {
        setIndustryLoading(true);
        const response = await axiosInstance.get(`${apiUrl}dropdowns/type/industry`);
        
        // Handle different possible response structures
        const options = response.data.data || response.data || [];
        
        // Ensure we have an array of options
        if (Array.isArray(options)) {
          setIndustryOptions(options);
        } else {
          console.warn('Industry options response is not an array:', options);
          setIndustryOptions([]);
        }
      } catch (error) {
        console.error("Error fetching industry options:", error);
        // Fallback to default options if API fails
        setIndustryOptions([
          { label: "Technology", value: "Technology" },
          { label: "Manufacturing", value: "Manufacturing" }
        ]);
      } finally {
        setIndustryLoading(false);
      }
    };

    fetchIndustryOptions();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const userId = JSON.parse(localStorage.getItem("info"))?.userId;
      const response = await GetAllData(`boss/allBosses?userId=${userId}`);
      const transformed = response.map(boss => ({
        ...boss,
        bossName: boss.bossName,
        companyName: boss.companyName,
      }));
      setData(transformed);
      setFilteredData(transformed);
      setLoader(false);

      // Open modal if bossId is present in URL
      const urlParams = new URLSearchParams(window.location.search);
      const bossId = urlParams.get("bossId");
      if (bossId) {
        const selected = transformed.find(b => b._id === bossId);
        if (selected) {
          setSelectedBoss(selected);
          setIsModalOpen(true);
        }
      }
    };
    fetchData();
  }, []);

  const handleSearch = async (filters) => {
    const userId = JSON.parse(localStorage.getItem("info"))?.userId;
    const token = localStorage.getItem("token");

    // Combine search fields into one text string
    const textQuery = [filters.bossName, filters.company, filters.industry]
      .filter(Boolean)
      .join(" ")
      .trim();

    // Check if user is actually searching
    const hasSearchTerms = Boolean(textQuery);
    setIsSearching(hasSearchTerms);

    // If all fields are empty, reset to original full data
    if (!hasSearchTerms) {
      setFilteredData(data);
      return;
    }

    try {
      const response = await axiosInstance.post(
        `${apiUrl}boss/searchAllBosses?userId=${userId}`,
        { text: textQuery },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = response.data.data;

      const transformed = result.map(boss => ({
        ...boss,
      }));

      setFilteredData(transformed);
    } catch (error) {
      console.error("Boss search failed:", error);
      setFilteredData([]); // Show "No bosses found"
    }
  };

  const handleViewDetails = (boss) => {
    setSelectedBoss(boss);
    setIsModalOpen(true);
  };

  // Helper function to determine what message to show
  const getNoDataMessage = () => {
    if (data.length === 0) {
      return "No bosses found";
    }
    if (isSearching && filteredData.length === 0) {
      return "No matching results found";
    }
    return null;
  };

  // Generate industry MenuItems dynamically
  const generateIndustryMenuItems = () => {
    if (industryLoading) {
      return [
        <MenuItem key="loading" disabled>
          <CircularProgress size={16} sx={{ marginRight: 1 }} />
          Loading...
        </MenuItem>
      ];
    }

    if (industryOptions.length === 0) {
      return [
        <MenuItem key="no-options" disabled>
          No options available
        </MenuItem>
      ];
    }

    return industryOptions.map((option) => {
      // Handle different possible option structures
      const value = option.value || option.name || option;
      const label = option.label || option.name || option;
      
      return (
        <MenuItem key={value} value={value}>
          {label}
        </MenuItem>
      );
    });
  };

  const noDataMessage = getNoDataMessage();

  if (loader) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <CircularProgress color="inherit" sx={{ color: "#12188A" }} />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Title>Bosses List</Title>
      <SearchBar
        fields={[
          { name: "bossName", placeholder: "Boss Name" },
          { name: "company", placeholder: "Company" },
          {
            name: "industry",
            placeholder: "Industry",
            props: {
              select: true,
              displayEmpty: true,
              children: generateIndustryMenuItems()
            }
          }
        ]}
        onSearch={handleSearch}
        buttonText="Search"
        buttonWidth="200px"
      />
      
      {noDataMessage ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <Typography style={{ color: '#64748B', fontSize: '14px' }}>
            {noDataMessage}
          </Typography>
        </Box>
      ) : (
        <>
          <div style={{ marginTop: "24px" }}>
            <BossesTable
              data={pageData}
              columns={columns}
              onActionClick={handleViewDetails}
              actionLabel="View Details"
              showAvatar={false}
              nameField="bossName"
            />
          </div>
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Pagination data={filteredData} setPageData={setPageData} itemsPerPage={10} />
          </Box>
        </>
      )}
      
      {selectedBoss && (
        <BossDetailsModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          bossDetails={selectedBoss}
          viewType="boss-list"
        />
      )}
    </PageContainer>
  );
};

export default BossesList;